import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useRef} from 'react';
import React from 'react';
import {CUSTOM_CONTROLS_WRAPPER} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {CustomOutlineBoxes} from '@Components/poster-editor/components/custom-item-controls/components/custom-outline-boxes/custom-outline-boxes';
import {SingleItemCustomControls} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls';
import {ActiveSelectionCustomControls} from '@Components/poster-editor/components/custom-item-controls/components/active-selection-custom-controls/active-selection-custom-controls';
import type {CustomItemControlsWrapperProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';
import {GlobalPosterEditorJqueryElement} from '@/components/poster-editor/poster-editor.types';
import {useShouldHideControlsWhenItemAnimating} from '@Hooks/poster-editor/useShouldHideControlsWhenItemAnimating';
import {useAppSelector} from '@/hooks';

export function CustomItemControlsWrapperMobile({...props}: CustomItemControlsWrapperProps): ReactElement<any> | null {
  const isTouchGestureBeingApplied = useAppSelector((state) => state.posterEditor.isTouchGestureBeingApplied);

  const hideSelectedOrHoveredItemControlsDueToAnimation = useShouldHideControlsWhenItemAnimating(props.pageHashedId);

  const wrapperRef = useRef<HTMLDivElement | null>(null);


  useEffect(() => {
    let animationFrameId: number | null = null;
    let mutationObserver: MutationObserver | null = null;

    const updateWrapperPosition = (pageContainer: HTMLElement): void => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }

      wrapper.style.top = `${pageContainer.offsetTop}px`;
      wrapper.style.left = `${pageContainer.offsetLeft}px`;
    };

    const startTrackingPosition = (pageContainer: HTMLElement): void => {
      const track = (): void => {
        updateWrapperPosition(pageContainer);

        animationFrameId = requestAnimationFrame(track);
      };

      track();
    };

    const observeCanvasContainerInsertion = (): void => {
      const rootToObserve = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER]?.get?.(0) ?? document.body;

      mutationObserver = new MutationObserver(() => {
        const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
        const pageContainer = page?.getCanvasContainer();

        if (pageContainer) {
          mutationObserver?.disconnect();

          startTrackingPosition(pageContainer);
        }
      });

      mutationObserver.observe(rootToObserve, {childList: true, subtree: true});
    };

    observeCanvasContainerInsertion();

    return (): void => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }

      mutationObserver?.disconnect();
    };
  }, []);

  if (hideSelectedOrHoveredItemControlsDueToAnimation) {
    return null;
  }

  if (isTouchGestureBeingApplied) {
    return null;
  }

  return (
    <div ref={wrapperRef} key={CUSTOM_CONTROLS_WRAPPER} id={props.pageHashedId} className={`${CUSTOM_CONTROLS_WRAPPER} _position-absolute`}>
      <CustomOutlineBoxes pageHashedId={props.pageHashedId} />
      <SingleItemCustomControls pageHashedId={props.pageHashedId} />
      <ActiveSelectionCustomControls pageHashedId={props.pageHashedId} />
    </div>
  );
}
